import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const { refreshUser } = useAuth();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let timer;
    const poll = async () => {
      if (attempts.current >= 8) { setStatus("timeout"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setStatus("paid"); refreshUser(); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setStatus("error"); return; }
      } catch (e) {}
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, refreshUser]);

  return (
    <div className="max-w-md mx-auto px-6 py-24 text-center">
      {status === "checking" && (<><Loader2 className="w-12 h-12 animate-spin text-accent mx-auto" /><h1 className="font-heading text-2xl font-bold mt-6">Confirming payment…</h1><p className="text-muted-foreground mt-2">This only takes a moment.</p></>)}
      {status === "paid" && (<>
        <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto"><CheckCircle2 className="w-9 h-9 text-emerald-600" /></div>
        <h1 className="font-heading text-2xl font-bold mt-6" data-testid="payment-success-title">You're all set!</h1>
        <p className="text-muted-foreground mt-2">Payment confirmed. Your plan or extra is now active in My garage.</p>
        <div className="flex gap-3 justify-center mt-8">
          <Button asChild className="bg-accent hover:bg-emerald-600 text-white" data-testid="go-sell-btn"><Link to="/sell">List a car</Link></Button>
          <Button asChild variant="outline"><Link to="/dashboard">Dashboard</Link></Button>
        </div>
      </>)}
      {(status === "error" || status === "timeout") && (<>
        <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto"><XCircle className="w-9 h-9 text-red-600" /></div>
        <h1 className="font-heading text-2xl font-bold mt-6">Payment not confirmed</h1>
        <p className="text-muted-foreground mt-2">{status === "timeout" ? "It's taking longer than expected. Check your dashboard shortly." : "Something went wrong with the payment."}</p>
        <Button asChild variant="outline" className="mt-8"><Link to="/pricing">Back to plans</Link></Button>
      </>)}
    </div>
  );
}
